import React, { useState, useMemo } from 'react';
import { CenterType } from '../../../../services/ConfigurationService/types/centerType';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { EditCenterDialog } from '../dialogs/EditCenterDialog';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface CenterGridRow {
  id: string;
  code: string;
  shortName: string;
  centerType: string;
  pas: string;
}

export function CenterGrid({ 
  centers, 
  onCenterClick, 
  onCenterSaved 
}: {
  centers: CenterType[];
  onCenterClick?: (center: CenterType) => void;
  onCenterSaved?: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter centers based on search term  
  const filteredCenters = useMemo(() => {
    if (!searchTerm.trim()) {
      return centers;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return centers.filter(center => 
      (center.code?.toLowerCase().includes(searchLower)) ||
      (center.shortName?.toLowerCase().includes(searchLower)) ||
      (center.centerType?.toLowerCase().includes(searchLower)) ||
      (center.pas?.toLowerCase().includes(searchLower))
    );
  }, [centers, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleRowSelection = (args: RowSelectEventArgs) => {
    if (args.data && onCenterClick) {
      const selectedRow = args.data as CenterGridRow;
      const center = centers.find(c => c.id === selectedRow.id);
      if (center) {
        onCenterClick(center);
      }
    }
  };

  if (centers.length === 0) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by code, name, type, PAS..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No centers found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No centers available for this company
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredCenters.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by code, name, type, PAS..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No centers match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by code, name, type, PAS..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                }
              }
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ color: '#64748b' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Grid */}
      <GridComponent
        dataSource={filteredCenters}
        height={500}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 25 }}
        selectionSettings={{ mode: 'Row', type: 'Single' }}
        rowSelected={handleRowSelection}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}
        enableHover={true}
      >
        <ColumnsDirective>
          <ColumnDirective 
            field="code" 
            headerText="Code" 
            width="100"
            textAlign="Left"
          />
          <ColumnDirective 
            field="shortName" 
            headerText="Short Name" 
            width="150"
            textAlign="Left"
          />
          <ColumnDirective 
            field="centerType" 
            headerText="Type" 
            width="120"
            textAlign="Left"
          />
          <ColumnDirective 
            field="pas" 
            headerText="PAS" 
            width="100"
            textAlign="Left"
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
    </Box>
  );
}
